/* eslint-disable react/display-name */
import React, { FunctionComponent } from 'react';
import { Linking, Image as ImageRN, Pressable, View, Platform, StyleSheet, Text, ActivityIndicator } from 'react-native';
import { IElementBaseProps, ModalFull, useInitializeElement } from '..';
import { MaterialIcons } from "@expo/vector-icons";
import ImageViewer from 'react-native-image-zoom-viewer';
import { useStores } from '../../models';
import { Icon } from "@gluestack-ui/themed";
import Spacer from '../base/Spacer';


const ShowcaseContent = React.memo(({ element, statusStore }: any) => {
    const [fullscreenImage, setFullScreenImage] = React.useState(false);
    
    const [sizeImg, setSizeImg] = React.useState({ height: 200, width: statusStore.windowDimensions.width - (statusStore.isLargeScreen ? 350 : 20) });
    const [loading, setLoading] = React.useState(true);

    const { image, logo, body, href: link, backgroundColor, backgroundColor2, textColor, textColor2, noteTitle } = element.templateData || {};
    const title = element?.name ?? element?.label ?? "";
    const imageUri = element?.templateData?.image || "";
    React.useEffect(() => {
        if (image) {
            ImageRN.getSize(image, (w, h) => {
                let widthImg = sizeImg.width;
                setSizeImg({
                    height: w < widthImg ? h : (widthImg / w * h),
                    width: w < widthImg ? w : widthImg
                });
                setLoading(false);
            });
        } else {
            setLoading(false);
        }
    }, [image]);

    if (loading) return <View style={styles.loadingContainer}><ActivityIndicator /></View>;

    return (
        <View style={styles.showcaseOuter}>
            {/* Header del Showcase */}
            <View style={[styles.headerRow, { backgroundColor: backgroundColor || "#fff" }]}>
                {logo ? <ImageRN style={styles.logo} source={{ uri: logo }} /> : null }
                <View style={styles.titleTextContainer}>
                    <Text style={[styles.boldText, { color: textColor || "#7a7373" }]}>{element.name || ''}</Text>
                    {noteTitle ? <Text style={[styles.noteText, { color: textColor || "#7a7373" }]}>{noteTitle}</Text> : null }
                </View>
            </View>

            {/* Imagen Principal */}
            {image ?  (
                <View style={[styles.imageContainer, { backgroundColor: backgroundColor2 || "#fff" }]}>
                    <Pressable onPress={() => setFullScreenImage(true)}>
                        <ImageRN
                            style={{ height: sizeImg.height, width: sizeImg.width, maxHeight: statusStore.windowDimensions.height / 2 }}
                            resizeMode="contain"
                            source={{ uri: image }}
                        />
                    </Pressable>
                </View>
            ) : null }

            {/* Cuerpo */}
            {body ?  (
                <View style={[styles.bodyContainer, { backgroundColor: backgroundColor2 || "#fff" }]}>
                    <Text style={[styles.justifyText, { color: textColor2 || "#7a7373" }]}>{body}</Text>
                </View>
            ) : null }

            {/* Link */}
            {link ? (
                <Pressable onPress={() => Linking.openURL(link)} style={styles.linkContainer}>
                    <Spacer />
                    <Text style={styles.linkText}>ABRIR </Text>
                    <Icon name="launch" as={MaterialIcons} size="md" color="$closed400" />
                </Pressable>
            ) : null }

            {/* Modal de Imagen (Solo se monta si es necesario) */}
            {fullscreenImage ?  (
                <ModalFull visible={fullscreenImage} title={title} closeButton_onPress={() => setFullScreenImage(false)}>
                    <View style={styles.fullScreenModal} >
                         <ImageRN
                            style={styles.fullImageWeb}
                            resizeMode="contain"
                            source={{ uri: image }}
                        />
                        {Platform.OS === "web" ? (
                            <ImageRN resizeMode="contain" source={{ uri: imageUri }} />
                        ) : (
                            <ImageViewer  enableImageZoom={true} saveToLocalByLongPress={false}
                        imageUrls={[{ url: image }]}
                        renderIndicator={() => <Text />}
                        menuContext={{ saveToLocal: 'Guardar', cancel: 'Cancelar' }} />
                        )}
                    </View>
                </ModalFull>
            ) : null }
        </View>
    );
});

export const TitleElement: FunctionComponent<IElementBaseProps> = ({ ...props }) => {
    const { statusStore } = useStores();
    const element = props.element;
    const templateData = element.templateData || {};
    if (templateData.hidden) return null;

    if (templateData.asShowCase) {
        return <ShowcaseContent element={element} statusStore={statusStore} />;
    }
    // Versión simple (SimpleTitle) optimizada
    return (
        <View style={[styles.simpleTitleContainer, { backgroundColor: templateData.backgroundColor || "#fff" }]}>
            <Text style={[styles.simpleTitleText, { color: templateData.textColor || "#000" }]}>
                {element.name || element.label}
            </Text>
            {templateData.href && (
                <>
                    <Spacer />
                    <Pressable onPress={() => Linking.openURL(templateData.href)} style={styles.simpleLink}>
                        <Icon name="launch" as={MaterialIcons} size="md" color="$closed400" />
                    </Pressable>
                </>
            )}
        </View>
    );
};



const styles = StyleSheet.create({
    loadingContainer: { height: 45, justifyContent: 'center' },
    showcaseOuter: { width: '100%', padding: 0 },
    headerRow: { flexDirection: 'row', alignItems: 'center', padding: 8, width: '100%' },
    logo: { width: 50, height: 50, borderRadius: 12, marginRight: 8 },
    titleTextContainer: { flexShrink: 1 },
    boldText: { fontWeight: 'bold', fontSize: 16 },
    noteText: { fontSize: 12 },
    imageContainer: { padding: 3, alignItems: 'center', justifyContent: 'center', width: '100%' },
    bodyContainer: { padding: 12, width: '100%' },
    justifyText: { textAlign: 'justify', fontSize: 14 },
    linkContainer: { flexDirection: 'row', alignItems: 'center', paddingRight: 15, marginBottom: 5 },
    linkText: { color: '#888', fontWeight: 'bold', fontSize: 14 }, // Reemplazar por color real de $closed400
    fullScreenModal: { width: '100%', height: '100%',flex: 1, backgroundColor: 'white' ,flexDirection: 'column', justifyContent: 'center', alignItems: 'center' },
    fullImageWeb: { width: '100%', height: '100%' },
    simpleTitleContainer: {
        width: '100%',
        minHeight: 45,
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: 8,
        paddingVertical: 8
    },
    simpleTitleText: { fontWeight: 'bold', fontSize: 16 },
    simpleLink: { paddingHorizontal: 10 }
});